import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';

import VM from 'openblock-vm';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import CommunityButton from './community-button.jsx'; // eslint-disable-line no-unused-vars
import ShareButton from './share-button.jsx'; // eslint-disable-line no-unused-vars
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import LanguageSelector from '../../containers/language-selector.jsx';
import SaveStatus from './save-status.jsx'; // eslint-disable-line no-unused-vars
import ProjectWatcher from '../../containers/project-watcher.jsx'; // eslint-disable-line no-unused-vars
import MenuBarMenu from './menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import AuthorInfo from './author-info.jsx';
import AccountNav from '../../containers/account-nav.jsx'; // eslint-disable-line no-unused-vars
import LoginDropdown from './login-dropdown.jsx'; // eslint-disable-line no-unused-vars
import SB3Downloader from '../../containers/sb3-downloader.jsx';
// MSTEM 2026-06-18: DeletionRestorer + TurboMode imports removed with the "Chỉnh sửa"
// (Edit) menu — neither is an mSTEM requirement. The containers remain in the codebase
// for any future use / BE re-wiring.
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import {isScratchDesktop} from '../../lib/isScratchDesktop';
import {UPDATE_MODAL_STATE} from '../../lib/update-state.js';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';

import {
    openTipsLibrary,
    openUploadProgress,
    openUpdateModal,
    openConnectionModal,
    openDeviceLibrary,
    openThemeManager,
    openRobotManager,
    openKitManager,
    openFirmwareWizard,
    // Phase 3 / Plan 03-09 — 6 new modal flag actions (PRJ-01..07 / LLD UC 06-12).
    openNewProjectModal as openNewProjectModalAction,
    openOpenProjectModal as openOpenProjectModalAction,
    openSaveProjectModal as openSaveProjectModalAction,
    openShareProjectModal as openShareProjectModalAction,
    openSharedProjectsModal as openSharedProjectsModalAction,
    openTutorialModal as openTutorialModalAction,
    // Phase 4 gap-closure — "Xem cấu tạo khối Robot ghép" Help-menu affordance
    // that opens the composite "what's inside" info modal (ROB-02/03/04).
    openRobotBlockInfo as openRobotBlockInfoAction,
    setRobotBlockInfoKey as setRobotBlockInfoKeyAction,
    // Phase 5 SMH-02 — "Dashboard giám sát thiết bị" Help-menu affordance.
    openSmartHomeDashboard as openSmartHomeDashboardAction,
    // Phase 6 / Plan 06-01 — 4 "Mở rộng" authoring modal opens (EXT-01..04).
    openDeviceDeclaration as openDeviceDeclarationAction,
    openBlockBuilder as openBlockBuilderAction,
    openLedDesigner as openLedDesignerAction,
    openPublishWizard as openPublishWizardAction,
    // UC-43 / UC-47 — "Quản lý ... mở rộng" manager modal opens.
    openExtDeviceManager as openExtDeviceManagerAction,
    openExtBlockManager as openExtBlockManagerAction,
    // Phase 7 AI-01 — "Bảng điều khiển AI" Help-menu affordance.
    openAiPanel as openAiPanelAction
} from '../../reducers/modals';
import {setPlayer} from '../../reducers/mode';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    remixProject,
    saveProjectAsCopy
} from '../../reducers/project-state';
// BLOCKER #3 (Plan 03-09): removed Scratch FSM save/new imports —
// Phase 3 menu-bar no longer triggers the project-state FSM save pathways.
// The FSM remains intact in reducers/project-state.js for non-menu-bar callers.
import {
    openAboutMenu,
    closeAboutMenu,
    aboutMenuOpen,
    openAccountMenu,
    closeAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    fileMenuOpen,
    openSettingMenu,
    closeSettingMenu,
    settingMenuOpen,
    openLanguageMenu,
    closeLanguageMenu,
    languageMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen,
    // Phase 3 / Plan 03-09 W-13 — Help dropdown mirrors fileMenuOpen pattern.
    openHelpMenu,
    closeHelpMenu,
    helpMenuOpen,
    // Phase 6 / Plan 06-01 W-0 — "Mở rộng" dropdown mirrors helpMenuOpen pattern.
    openExtMenu,
    closeExtMenu,
    extMenuOpen,
    // menu-bar-modernization — "Quản lý" dropdown mirrors extMenuOpen pattern.
    openManageMenu,
    closeManageMenu,
    manageMenuOpen
} from '../../reducers/menus';
// Phase 3 / Plan 03-09 — tutorial selection + Phase 3 save handler imports.
import {openTutorial} from '../../reducers/tutorial-modal';
// BLOCKER #2 (Plan 03-09): byte cap imported from the single source of truth
// in mstem-save-handler — never inlined as a literal in this file.
import {MAX_VM_STATE_BYTES} from '../../lib/mstem-save-handler';
import {
    writeMyProjects,
    updateMyProject,
    getMyProjectById
} from '../../lib/mstem-project-storage';
import {updateProjectEntry} from '../../reducers/my-projects';
// Tutorial first-chapter seed (Plan 03-07b + 03-08 data).
import IDE_CHAPTERS from '../../lib/mstem-mock/tutorials/ide-chapters';
import FIRMWARE_CHAPTERS from '../../lib/mstem-mock/tutorials/firmware-chapters';
import log from '../../lib/log';
// MSTEM 2026-06-18: setStageSize + STAGE_SIZE_MODES imports removed with the Program Mode
// switch handler (the only caller). setUploadMode/setRealtimeMode are KEPT — still used by
// the PROGRAM_MODE_UPDATE listener (handleProgramModeUpdate).
import {setUploadMode, setRealtimeMode} from '../../reducers/program-mode';
import {setRealtimeConnection, clearConnectionModalPeripheralName} from '../../reducers/connection-modal';
import {setUpdate} from '../../reducers/update';

import collectMetadata from '../../lib/collect-metadata';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg'; // eslint-disable-line no-unused-vars
// import mystuffIcon from './icon--mystuff.png';
// import profileIcon from './icon--profile.png';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import languageIcon from '../language-selector/language-icon.svg';
import aboutIcon from './icon--about.svg';
import saveIcon from './icon--save.svg';
// import linkSocketIcon from './icon--link-socket.svg';
// import communityIcon from './icon--community.svg';
// menu-bar-modernization: wikiIcon no longer rendered (standalone wiki button
// removed; "Tài liệu mSTEM" moved into the Trợ giúp dropdown as a text item).
// Kept imported (eslint-disabled) so the asset stays referenced for BE team.
import wikiIcon from './icon--wiki.svg'; // eslint-disable-line no-unused-vars
import fileIcon from './icon--file.svg';
// MSTEM 2026-06-18: editIcon removed with the "Chỉnh sửa" (Edit) menu (its only user).

import openblockLogo from './openblock-logo.svg';
import openblockLogoSmall from './openblock-logo-small.svg';

import sharedMessages from '../../lib/shared-messages';

// MSTEM 2026-06-18: react-switch import removed with the Program Mode toggle (its only user).

import deviceIcon from './icon--device.svg';
import unconnectedIcon from './icon--unconnected.svg';
import connectedIcon from './icon--connected.svg';
import screenshotIcon from './icon--screenshot.svg';
import settingIcon from './icon--setting.svg';
import themeIcon from './icon--theme.svg';

import uploadFirmwareIcon from './icon--upload-firmware.svg';
// Phase 3 / Plan 03-09 — 4 new menu item icons (white fill on navy per micro-2.1).
import helpMenuIcon from './icon--help.svg';
import shareIcon from './icon--share.svg';
import folderOpenIcon from './icon--folder-open.svg';
import sharedFolderIcon from './icon--shared-folder.svg';
import saveSvgAsPng from 'openblock-save-svg-as-png';
import {showAlertWithTimeout} from '../../reducers/alerts';

const ariaMessages = defineMessages({
    language: {
        id: 'gui.menuBar.LanguageSelector',
        defaultMessage: 'language selector',
        description: 'accessibility text for the language selection menu'
    },
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button'
    },
    community: {
        id: 'gui.menuBar.community',
        defaultMessage: 'Open Community',
        description: 'accessibility text for the community button'
    },
    wiki: {
        id: 'gui.menuBar.wiki',
        defaultMessage: 'Wiki',
        description: 'accessibility text for the wiki button'
    },
    // Phase 3 / Plan 03-09 — Help menu (PRJ-06 + PRJ-07 / LLD UC 09-10).
    help: {
        id: 'gen.menuBar.helpAria',
        defaultMessage: 'Trợ giúp',
        description: 'Accessibility label for help menu'
    }
});

// MSTEM-ADDED Phase 2 — manager labels. First created by Plan 02-03 (Robot
// Manager direct entry). Later plans 04/05/06 will extend with kit/firmware
// entries, so keep the export name + this comment intact.
const mstemMessages = defineMessages({
    manageRobotShort: {
        defaultMessage: 'Robot',
        description: 'Short label on the Robot Manager menu-bar button',
        id: 'gen.menuBar.manageRobotShort'
    },
    manageKitShort: {
        defaultMessage: 'Kit',
        description: 'Short label on the Kit Manager menu-bar button',
        id: 'gen.menuBar.manageKitShort'
    },
    adminBadge: {
        defaultMessage: 'Admin',
        description: 'Role indicator badge in menu-bar header (DG-06)',
        id: 'gen.menuBar.adminBadge'
    },
    // menu-bar-modernization — "Quản lý" consolidation dropdown labels.
    // Groups Robot Manager + Kit (SmartHome) Manager + Upload Firmware under
    // one top-level trigger to keep the header from overflowing.
    manageMenu: {
        defaultMessage: 'Quản lý',
        description: 'Trigger label for the consolidated management dropdown',
        id: 'gen.menuBar.manageMenu'
    },
    manageMenuAria: {
        defaultMessage: 'Quản lý',
        description: 'Accessibility label for the management dropdown',
        id: 'gen.menuBar.manageMenuAria'
    },
    manageRobot: {
        defaultMessage: 'Quản lý Robot',
        description: 'Manage menu item: open the Robot Manager modal',
        id: 'gen.menuBar.manageRobot'
    },
    manageKit: {
        defaultMessage: 'Quản lý Kit (SmartHome)',
        description: 'Manage menu item: open the Kit / SmartHome Manager modal',
        id: 'gen.menuBar.manageKit'
    },
    manageFirmware: {
        defaultMessage: 'Upload Firmware',
        description: 'Manage menu item: open the firmware upload wizard',
        id: 'gen.menuBar.manageFirmware'
    }
});

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom'
}) => {
    if (enable) {
        return (
            <React.Fragment>
                {children}
            </React.Fragment>
        );
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};


MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
};

const MenuItemTooltip = ({id, isRtl, children, className}) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool
};

const AboutButton = props => (
    <Button
        className={classNames(styles.menuBarItem, styles.hoverable)}
        iconClassName={styles.aboutIcon}
        iconSrc={aboutIcon}
        onClick={props.onClick}
    />
);

AboutButton.propTypes = {
    onClick: PropTypes.func.isRequired
};

class MenuBar extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'checkOverflow',
            'containerRef',
            'handleClickNew',
            'handleClickRemix',
            'handleClickOpenCommunity',
            'handleClickOpenWiki',
            'handleClickSaveAsCopy',
            'handleClickSeeCommunity',
            // BLOCKER #1 (Plan 03-09): Phase 3 share handler renamed
            // handleClickShareProject. Existing Scratch handleClickShare
            // (with waitForUpdate arg, used by ShareButton community flow)
            // is PRESERVED unchanged below — still bound here for any
            // residual callers.
            'handleClickShare',
            // Phase 3 / Plan 03-09 — File menu (PRJ-01..05) + Help menu (PRJ-06..07).
            'handleClickOpenProject',
            'handleClickSavePhase3',
            'handleClickShareProject',
            'handleClickSharedProjects',
            'handleClickHelpIde',
            'handleClickHelpFirmware',
            'handleClickHelpRobotComposite',
            'handleClickSmartHomeDashboard',
            'handleClickAiPanel',
            'handleHelpMouseUp',
            // Phase 6 / Plan 06-01 — "Mở rộng" dropdown + 4 item handlers.
            'handleExtMouseUp',
            'handleClickDeclareDevice',
            'handleClickBuildBlock',
            'handleClickLedDesigner',
            'handleClickPublish',
            'handleClickManageDevices',
            'handleClickManageBlocks',
            // menu-bar-modernization — "Quản lý" dropdown trigger + 3 items.
            'handleManageMouseUp',
            'handleClickManageRobot',
            'handleClickManageKit',
            'handleClickManageFirmware',
            'handleKeyPress',
            'handleLanguageMouseUp',
            'getSaveToComputerHandler',
            'handleConnectionMouseUp',
            'handleUploadFirmware',
            'handleWindowsResize',
            'handleSelectDeviceMouseUp',
            'handleProgramModeUpdate',
            'handleScreenshot',
            'handleCheckUpdate',
            'handleClearCache'
        ]);
        this.state = {
            isOverflow: false
        };
    }
    componentDidMount () {
        document.addEventListener('keydown', this.handleKeyPress);
        this.props.vm.on('PERIPHERAL_DISCONNECTED', this.props.onDisconnect);
        this.props.vm.on('PROGRAM_MODE_UPDATE', this.handleProgramModeUpdate);
        window.addEventListener('resize', this.handleWindowsResize);
    }
    componentDidUpdate (prevProps) {
        if (prevProps.isToolboxUpdating !== this.props.isToolboxUpdating && !this.state.isOverflow
        ) {
            this.checkOverflow();
        }
    }
    componentWillUnmount () {
        document.removeEventListener('keydown', this.handleKeyPress);
        this.props.vm.removeListener('PERIPHERAL_DISCONNECTED', this.props.onDisconnect);
        this.props.vm.removeListener('PROGRAM_MODE_UPDATE', this.handleProgramModeUpdate);
        window.removeEventListener('resize', this.handleWindowsResize);
    }
    handleWindowsResize () {
        this.setState({isOverflow: false});
        if (this.resizeTimerout) {
            clearTimeout(this.resizeTimerout);
        }
        // When you continue to drag and resize the window, the menu content is not hidden immediately,
        // but delayed for a period of time to prevent the menu content from flickering frequently.After
        // testing, a delay of 300ms seems to be the most comfortable.
        this.resizeTimerout = setTimeout(() => this.checkOverflow(), 300);
    }
    containerRef (el) {
        this.containerElement = el;
    }
    checkOverflow () {
        if (this.containerElement) {
            const container = this.containerElement;
            // MSTEM-MODIFIED: Phase 1 micro-fix 01-07 — desktop-first policy (arch_decision_desktop_first).
            // Upstream OpenBlock collapses the MenuBar into icon-only "narrow mode" whenever
            // container.scrollWidth > clientWidth, which fires spuriously on initial mount before
            // fonts/layout settle, producing a tiny-logo + icon-only flash until the user clicks a tab.
            // For viewports >= 1024px we force wide mode (text labels + full logo) because the project
            // is desktop-first; mobile (<1024px) retains the original overflow-collapse behaviour.
            const viewportWidth = (typeof window !== 'undefined' && window.innerWidth) || 0;
            if (viewportWidth >= 1024) {
                this.setState({isOverflow: false});
                return;
            }
            this.setState({isOverflow: container.scrollWidth > container.clientWidth});
        }
    }
    handleClickNew () {
        // Phase 3 / Plan 03-09 (BLOCKER #3) — replaced Scratch FSM "new project"
        // pathway with the NewProjectModal (PRJ-01). The modal handles validation
        // and dispatches createProjectEntry on confirm.
        this.props.onRequestCloseFile();
        this.props.onOpenNewProjectModal();
    }
    handleClickRemix () {
        this.props.onClickRemix();
        this.props.onRequestCloseFile();
    }
    // Phase 3 / Plan 03-09 — File menu PRJ-02: open MyProjects browser.
    handleClickOpenProject () {
        this.props.onRequestCloseFile();
        this.props.onOpenOpenProjectModal();
    }
    // Phase 3 / Plan 03-09 — File menu PRJ-03: quick-save current project.
    // BLOCKER #2 RESOLUTION:
    //   - Reads vm + myProjectsList + currentProjectId from props (mapStateToProps).
    //   - Dispatches via props (mapDispatchToProps).
    //   - Imports the byte cap from mstem-save-handler — NEVER inlined here.
    //   - NO direct store access — pure connect()-based wiring.
    handleClickSavePhase3 () {
        this.props.onRequestCloseFile();
        const {vm, myProjectsList, currentProjectId} = this.props;
        // First-save path → open SaveProjectModal so user can name + describe.
        if (!currentProjectId) {
            this.props.onOpenSaveProjectModal();
            return;
        }
        const existing = getMyProjectById(myProjectsList, currentProjectId);
        if (!existing) {
            // Entry was deleted externally — fall back to first-save modal.
            this.props.onOpenSaveProjectModal();
            return;
        }
        try {
            const vmState = vm.toJSON();
            if (typeof vmState === 'string' && vmState.length > MAX_VM_STATE_BYTES) {
                this.props.onShowTooLarge();
                return;
            }
            const updated = updateMyProject(existing, {vmState: vmState});
            const nextList = myProjectsList.map(p => (
                p.id === currentProjectId ? updated : p
            ));
            let quotaHit = false;
            const ok = writeMyProjects(nextList, () => {
                quotaHit = true;
            });
            if (!ok || quotaHit) {
                this.props.onShowQuotaFull();
                return;
            }
            this.props.onUpdateEntry(updated);
            this.props.onShowSaved();
        } catch (err) {
            log.warn('handleClickSavePhase3 error', err);
        }
    }
    // Phase 3 / Plan 03-09 (BLOCKER #1) — Phase 3 share handler, distinct
    // name from legacy handleClickShare(waitForUpdate) which is preserved
    // above. Opens the ShareProjectModal (PRJ-04).
    handleClickShareProject () {
        this.props.onRequestCloseFile();
        this.props.onOpenShareProjectModal();
    }
    // Phase 3 / Plan 03-09 — File menu PRJ-05: browse projects shared with me.
    handleClickSharedProjects () {
        this.props.onRequestCloseFile();
        this.props.onOpenSharedProjectsModal();
    }
    // Phase 3 / Plan 03-09 — Help menu PRJ-06: IDE tutorial deck.
    handleClickHelpIde () {
        const firstChapter = (IDE_CHAPTERS[0] && IDE_CHAPTERS[0].id) || null;
        this.props.onRequestCloseHelp();
        this.props.onOpenTutorialModal();
        this.props.onOpenTutorial('ide', firstChapter);
    }
    // Phase 3 / Plan 03-09 — Help menu PRJ-07: Firmware tutorial deck.
    handleClickHelpFirmware () {
        const firstChapter = (FIRMWARE_CHAPTERS[0] && FIRMWARE_CHAPTERS[0].id) || null;
        this.props.onRequestCloseHelp();
        this.props.onOpenTutorialModal();
        this.props.onOpenTutorial('firmware', firstChapter);
    }
    handleHelpMouseUp () {
        if (!this.props.helpMenuOpen) {
            this.props.onClickHelp();
        }
    }
    // Phase 4 gap-closure (ROB-02..04) — opens the composite "what's inside"
    // info modal (defaults to lineFollow; the modal's tab row lets the student
    // switch between the 3 composites). This is the wired "Xem bên trong"
    // affordance the verifier flagged as missing.
    handleClickHelpRobotComposite () {
        this.props.onRequestCloseHelp();
        this.props.onSetRobotBlockInfoKey('lineFollow');
        this.props.onOpenRobotBlockInfo();
    }
    // Phase 5 SMH-02 — opens the "Dashboard giám sát thiết bị" monitoring modal.
    handleClickSmartHomeDashboard () {
        this.props.onRequestCloseHelp();
        this.props.onOpenSmartHomeDashboard();
    }
    // Phase 7 AI-01 — opens the "Bảng điều khiển AI" control panel modal.
    handleClickAiPanel () {
        this.props.onRequestCloseHelp();
        this.props.onOpenAiPanel();
    }
    // Phase 6 / Plan 06-01 — "Mở rộng" dropdown trigger + 4 item handlers (EXT-01..04).
    handleExtMouseUp () {
        if (!this.props.extMenuOpen) {
            this.props.onClickExt();
        }
    }
    handleClickDeclareDevice () {
        this.props.onRequestCloseExt();
        this.props.onOpenDeviceDeclaration();
    }
    handleClickBuildBlock () {
        this.props.onRequestCloseExt();
        this.props.onOpenBlockBuilder();
    }
    handleClickLedDesigner () {
        this.props.onRequestCloseExt();
        this.props.onOpenLedDesigner();
    }
    handleClickPublish () {
        this.props.onRequestCloseExt();
        this.props.onOpenPublishWizard();
    }
    // UC-43 / UC-47 — open the two "Quản lý ... mở rộng" manager modals.
    handleClickManageDevices () {
        this.props.onRequestCloseExt();
        this.props.onOpenExtDeviceManager();
    }
    handleClickManageBlocks () {
        this.props.onRequestCloseExt();
        this.props.onOpenExtBlockManager();
    }
    // menu-bar-modernization — "Quản lý" dropdown trigger + 3 consolidated items.
    // These reuse the EXISTING manager/firmware actions verbatim; only the entry
    // point moved from 3 standalone top-level buttons into one dropdown.
    handleManageMouseUp () {
        if (!this.props.manageMenuOpen) {
            this.props.onClickManage();
        }
    }
    handleClickManageRobot () {
        this.props.onRequestCloseManage();
        this.props.onOpenRobotManager();
    }
    handleClickManageKit () {
        this.props.onRequestCloseManage();
        this.props.onOpenKitManager();
    }
    handleClickManageFirmware () {
        this.props.onRequestCloseManage();
        this.props.onOpenFirmwareWizard();
    }
    handleClickSaveAsCopy () {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
    }
    handleClickSeeCommunity (waitForUpdate) {
        if (this.props.shouldSaveBeforeTransition()) {
            this.props.autoUpdateProject(); // save before transitioning to project page
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    handleClickShare (waitForUpdate) {
        if (!this.props.isShared) {
            if (this.props.canShare) { // save before transitioning to project page
                this.props.onShare();
            }
            if (this.props.canSave) { // save before transitioning to project page
                this.props.autoUpdateProject();
                waitForUpdate(true); // queue the transition to project page
            } else {
                waitForUpdate(false); // immediately transition to project page
            }
        }
    }
    handleKeyPress (event) {
        // Phase 3 / Plan 03-09 (BLOCKER #3) — replaced FSM save dispatch with the
        // Phase 3 quick-save handler. Adds Ctrl+N + Ctrl+Shift+S shortcuts per DP-15.
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (!modifier) return;
        // Ctrl+Shift+S → open ShareProjectModal (PRJ-04). Must come before
        // the plain Ctrl+S branch so Shift modifier wins.
        if ((event.key === 'S' || event.key === 's') && event.shiftKey) {
            this.handleClickShareProject();
            event.preventDefault();
            return;
        }
        // Ctrl+S → Phase 3 quick-save (PRJ-03). Mitigates T-03-09-01 (DoS
        // via default browser Save Page dialog) via preventDefault.
        if (event.key === 's' && !event.shiftKey) {
            this.handleClickSavePhase3();
            event.preventDefault();
            return;
        }
        // Ctrl+N → open NewProjectModal (PRJ-01).
        if (event.key === 'n' && !event.shiftKey) {
            this.handleClickNew();
            event.preventDefault();
        }
    }
    getSaveToComputerHandler (downloadProjectCallback) {
        return () => {
            this.props.onRequestCloseFile();
            downloadProjectCallback();
            if (this.props.onProjectTelemetryEvent) {
                const metadata = collectMetadata(this.props.vm, this.props.projectTitle, this.props.locale);
                this.props.onProjectTelemetryEvent('projectDidSave', metadata);
            }
        };
    }
    handleLanguageMouseUp (e) {
        if (!this.props.languageMenuOpen) {
            this.props.onClickLanguage(e);
        }
    }
    handleClickOpenCommunity () {
        // MSTEM-MODIFIED: Phase 1 FND-04 D-15 — replace OpenBlock community link with mSTEM placeholder
        // (matches D-13 Wiki pattern — placeholder URL until DFT delivers community platform)
        window.open('https://community.mstem.dft.vn');
    }
    handleClickOpenWiki () {
        // MSTEM-MODIFIED: Phase 1 FND-04 D-13 — point to mSTEM documentation placeholder
        window.open('https://docs.mstem.dft.vn');
    }
    handleConnectionMouseUp () {
        if (this.props.deviceId) {
            this.props.onOpenConnectionModal();
        } else {
            this.props.onDeviceIsEmpty();
        }
    }
    handleSelectDeviceMouseUp () {
        const blocklyBlockCanvas = document.querySelector('.blocklyWorkspace .blocklyBlockCanvas');
        if (blocklyBlockCanvas.childNodes.length === 0) {
            this.props.onOpenDeviceLibrary();
        } else {
            this.props.onWorkspaceIsNotEmpty();
        }
    }
    handleProgramModeUpdate (data) {
        if (data.isRealtimeMode) {
            this.props.onSetRealtimeMode();
        } else {
            this.props.onSetUploadMode();
        }
    }
    // MSTEM-NOTE Phase 2 GEN-05: handleUploadFirmware is preserved but no
    // longer wired to the Upload Firmware button — the button now opens the
    // mock FirmwareWizardModal via this.props.onOpenFirmwareWizard. BE team
    // will re-wire this method to vm.uploadFirmwareToPeripheral when real
    // hardware integration lands (see Plan 02-05 SUMMARY).
    handleUploadFirmware () {
        if (this.props.deviceId) {
            this.props.vm.uploadFirmwareToPeripheral(this.props.deviceId);
            this.props.onSetRealtimeConnection(false);
            this.props.onOpenUploadProgress();
        } else {
            this.props.onNoPeripheralIsConnected();
        }
    }
    handleScreenshot () {
        const blocklyBlockCanvas = document.querySelector('.blocklyWorkspace .blocklyBlockCanvas');
        if (blocklyBlockCanvas.childNodes.length === 0) {
            this.props.onWorkspaceIsEmpty();
        } else {
            const transform = blocklyBlockCanvas.getAttribute('transform');
            const scale = parseFloat(transform.substring(transform.indexOf('scale') + 6, transform.length - 1));
            const data = new Date();

            saveSvgAsPng.saveSvgAsPng(blocklyBlockCanvas, `${this.props.projectTitle}-${data.getTime()}.png`, {
                left: blocklyBlockCanvas.getBBox().x * scale,
                top: blocklyBlockCanvas.getBBox().y * scale,
                height: blocklyBlockCanvas.getBBox().height * scale,
                width: blocklyBlockCanvas.getBBox().width * scale,
                scale: 2 / scale,
                encoderOptions: 1
            });
        }
    }
    handleCheckUpdate () {
        this.props.onSetUpdate({phase: UPDATE_MODAL_STATE.checkingApplication});
        this.props.onClickCheckUpdate();
    }
    handleClearCache () {
        const readyClearCache = this.props.confirmClearCache(
            this.props.intl.formatMessage(sharedMessages.clearCacheWarning)
        );
        if (readyClearCache) {
            this.props.onClickClearCache();
        }
    }
    buildAboutMenu (onClickAbout) {
        if (!onClickAbout) {
            // hide the button
            return null;
        }
        if (typeof onClickAbout === 'function') {
            // make a button which calls a function
            return <AboutButton onClick={onClickAbout} />;
        }
        // assume it's an array of objects
        // each item must have a 'title' FormattedMessage and a 'handleClick' function
        // generate a menu with items for each object in the array
        return (
            onClickAbout.map(itemProps => (
                <MenuItem
                    key={itemProps.title}
                    isRtl={this.props.isRtl}
                    onClick={this.wrapAboutMenuCallback(itemProps.onClick)}
                >
                    {itemProps.title}
                </MenuItem>
            ))
        );
    }
    wrapAboutMenuCallback (callback) {
        return () => {
            callback();
            this.props.onRequestCloseAbout();
        };
    }
    render () {
        // BLOCKER #3 (Plan 03-09): removed `saveNowMessage` constant — the
        // "Save now" File menu item was the Scratch FSM trigger and has been
        // replaced by the Phase 3 "Lưu dự án" item (handleClickSavePhase3).
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        // BLOCKER #3 (Plan 03-09): removed `newProjectMessage` constant — the
        // "New" item is replaced by the Phase 3 "Tạo dự án mới" item.
        const checkUpdate = (
            <FormattedMessage
                defaultMessage="Check update"
                description="Menu bar item for check update"
                id="gui.menuBar.checkUpdate"
            />
        );
        const installDriver = (
            <FormattedMessage
                defaultMessage="Install driver"
                description="Menu bar item for install drivers"
                id="gui.menuBar.installDriver"
            />
        );
        const clearCache = (
            <FormattedMessage
                defaultMessage="Clear cache and restart"
                description="Menu bar item for clear cache and restart"
                id="gui.menuBar.clearCacheAndRestart"
            />
        );
        // eslint-disable-next-line no-unused-vars
        const remixButton = (
            <Button
                className={classNames(
                    styles.menuBarButton,
                    styles.remixButton
                )}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                onClick={this.handleClickRemix}
            >
                {remixMessage}
            </Button>
        );
        // Show the About button only if we have a handler for it (like in the desktop app)
        const aboutButton = this.buildAboutMenu(this.props.onClickAbout);
        return (
            <Box
                className={classNames(
                    this.props.className,
                    styles.menuBar
                )}
                componentRef={this.containerRef}
            >
                <div className={styles.mainMenu}>
                    <div className={classNames(styles.menuBarItem)}>
                        <img
                            alt="mSTEM"
                            className={classNames(styles.openblockLogo, {
                                [styles.clickable]: typeof this.props.onClickLogo !== 'undefined'
                            })}
                            draggable={false}
                            src={this.state.isOverflow ? this.props.logoSmall : this.props.logo}
                            onClick={this.props.onClickLogo}
                        />
                    </div>
                    {(this.props.canChangeLanguage) && (<div
                        className={classNames(styles.menuBarItem, styles.hoverable, styles.languageMenu)}
                    >
                        <div>
                            <img
                                className={styles.languageIcon}
                                src={languageIcon}
                            />
                            <img
                                className={styles.languageCaret}
                                src={dropdownCaret}
                            />
                        </div>
                        <LanguageSelector label={this.props.intl.formatMessage(ariaMessages.language)} />
                    </div>)}
                    {(this.props.canManageFiles) && (
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, {
                                [styles.active]: this.props.fileMenuOpen
                            })}
                            onMouseUp={this.props.onClickFile}
                        >
                            {this.state.isOverflow ? (
                                <img
                                    className={styles.fileIcon}
                                    src={fileIcon}
                                />) :
                                <FormattedMessage
                                    defaultMessage="File"
                                    description="Text for file dropdown menu"
                                    id="gui.menuBar.file"
                                />}
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.fileMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                                onRequestClose={this.props.onRequestCloseFile}
                            >
                                {/* Phase 3 / Plan 03-09 — DP-15 File menu (7 items in order).
                                    BLOCKER #3 fix: Scratch FSM "Save now" + "New" items
                                    REMOVED. Existing SB3Downloader + SBFileUploaderHOC
                                    items KEPT. */}
                                <MenuSection>
                                    {/* 1. Tạo dự án mới (Ctrl+N) — PRJ-01 */}
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClickNew}
                                    >
                                        <img
                                            className={styles.fileMenuIcon}
                                            draggable={false}
                                            src={folderOpenIcon}
                                            alt=""
                                        />
                                        <FormattedMessage
                                            defaultMessage="Tạo dự án mới"
                                            description="File menu: New Project (PRJ-01)"
                                            id="gen.menuBar.newProject"
                                        />
                                        <span className={styles.shortcut}>{'Ctrl+N'}</span>
                                    </MenuItem>
                                    {/* 2. Mở dự án có sẵn — PRJ-02 */}
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClickOpenProject}
                                    >
                                        <img
                                            className={styles.fileMenuIcon}
                                            draggable={false}
                                            src={folderOpenIcon}
                                            alt=""
                                        />
                                        <FormattedMessage
                                            defaultMessage="Mở dự án có sẵn"
                                            description="File menu: Open Project (PRJ-02)"
                                            id="gen.menuBar.openProject"
                                        />
                                    </MenuItem>
                                </MenuSection>
                                {(this.props.canCreateCopy || this.props.canRemix) && (
                                    <MenuSection>
                                        {this.props.canCreateCopy && (
                                            <MenuItem onClick={this.handleClickSaveAsCopy}>
                                                {createCopyMessage}
                                            </MenuItem>
                                        )}
                                        {this.props.canRemix && (
                                            <MenuItem onClick={this.handleClickRemix}>
                                                {remixMessage}
                                            </MenuItem>
                                        )}
                                    </MenuSection>
                                )}
                                <MenuSection>
                                    {/* 3. Mở từ máy tính — kept (Scratch SBFileUploaderHOC) */}
                                    <MenuItem
                                        onClick={this.props.onStartSelectingFileUpload}
                                    >
                                        {this.props.intl.formatMessage(sharedMessages.loadFromComputerTitle)}
                                    </MenuItem>
                                    {/* 4. Lưu dự án (Ctrl+S) — PRJ-03 (replaces FSM "Save now") */}
                                    <MenuItem onClick={this.handleClickSavePhase3}>
                                        <FormattedMessage
                                            defaultMessage="Lưu dự án"
                                            description="File menu: Save Project (PRJ-03)"
                                            id="gen.menuBar.saveProject"
                                        />
                                        <span className={styles.shortcut}>{'Ctrl+S'}</span>
                                    </MenuItem>
                                    {/* 5. Lưu về máy tính — kept (Scratch SB3Downloader) */}
                                    <SB3Downloader>{(className, downloadProjectCallback) => (
                                        <MenuItem
                                            className={className}
                                            onClick={this.getSaveToComputerHandler(downloadProjectCallback)}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Save to your computer"
                                                description="Menu bar item for downloading a project to your computer" // eslint-disable-line max-len
                                                id="gui.menuBar.downloadToComputer"
                                            />
                                        </MenuItem>
                                    )}</SB3Downloader>
                                </MenuSection>
                                <MenuSection>
                                    {/* 6. Chia sẻ dự án (Ctrl+Shift+S) — PRJ-04 */}
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClickShareProject}
                                    >
                                        <img
                                            className={styles.fileMenuIcon}
                                            draggable={false}
                                            src={shareIcon}
                                            alt=""
                                        />
                                        <FormattedMessage
                                            defaultMessage="Chia sẻ dự án"
                                            description="File menu: Share Project (PRJ-04)"
                                            id="gen.menuBar.shareProject"
                                        />
                                        <span className={styles.shortcut}>{'Ctrl+Shift+S'}</span>
                                    </MenuItem>
                                    {/* 7. Dự án được chia sẻ với tôi — PRJ-05 */}
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClickSharedProjects}
                                    >
                                        <img
                                            className={styles.fileMenuIcon}
                                            draggable={false}
                                            src={sharedFolderIcon}
                                            alt=""
                                        />
                                        <FormattedMessage
                                            defaultMessage="Dự án được chia sẻ với tôi"
                                            description="File menu: Shared Projects (PRJ-05)"
                                            id="gen.menuBar.sharedProjects"
                                        />
                                    </MenuItem>
                                </MenuSection>
                            </MenuBarMenu>
                        </div>
                    )}
                    {/* MSTEM 2026-06-18: "Chỉnh sửa" (Edit) menu REMOVED. It only exposed
                        stock Scratch DeletionRestorer (Khôi phục) + TurboMode (Chế độ Turbo),
                        neither of which is an mSTEM requirement. The underlying VM undo/turbo
                        capabilities remain intact — only this menu-bar UI affordance is gone. */}
                    <Divider className={classNames(styles.divider)} />
                    <div
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onMouseUp={this.handleSelectDeviceMouseUp}
                    >
                        <img
                            className={styles.deviceIcon}
                            src={deviceIcon}
                        />
                        {
                            this.props.deviceName ? (
                                <span>
                                    {this.props.deviceName}
                                </span>
                            ) : (
                                <FormattedMessage
                                    defaultMessage="No device selected"
                                    description="Text for menubar no device select button"
                                    id="gui.menuBar.noDeviceSelected"
                                />
                            )}
                    </div>
                    <Divider className={classNames(styles.divider)} />
                    <div
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onMouseUp={this.handleConnectionMouseUp}
                    >
                        {this.props.peripheralName ? (
                            <React.Fragment>
                                <img
                                    className={styles.connectedIcon}
                                    src={connectedIcon}
                                />
                                {this.state.isOverflow ? null : this.props.peripheralName}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <img
                                    className={styles.unconnectedIcon}
                                    src={unconnectedIcon}
                                />
                                {this.state.isOverflow ? null : <FormattedMessage
                                    defaultMessage="Unconnected"
                                    description="Text for menubar unconnected button"
                                    id="gui.menuBar.noConnection"
                                />}
                            </React.Fragment>
                        )}
                    </div>
                    {/* <div
                        className={classNames(styles.menuBarItem)}
                    >
                        <img
                            className={classNames(styles.linkSocketIcon)}
                            src={linkSocketIcon}
                        />
                    </div>*/}
                </div>
                {this.state.isOverflow ? null :
                    (<div className={styles.fileMenu}>
                        {/* MSTEM 2026-06-18: project-title input relocated out of the crowded menu bar
                            into the second toolbar row (centered above the blocks workspace) — see
                            src/components/gui/gui.jsx <ProjectTitleInput>. Only the author-info fallback
                            (for shared/remixed projects the viewer cannot edit) remains here. */}
                        {((this.props.authorUsername && this.props.authorUsername !== this.props.username) ? (
                            <AuthorInfo
                                className={styles.authorInfo}
                                imageUrl={this.props.authorThumbnailUrl}
                                projectTitle={this.props.projectTitle}
                                userId={this.props.authorId}
                                username={this.props.authorUsername}
                            />
                        ) : null)}
                        {(this.props.canManageFiles) && (
                            <SB3Downloader>{(className, downloadProjectCallback) => (
                                <div
                                    className={classNames(styles.menuBarItem, styles.hoverable)}
                                    onClick={this.getSaveToComputerHandler(downloadProjectCallback)}
                                >
                                    <img
                                        className={styles.saveIcon}
                                        src={saveIcon}
                                    />
                                </div>
                            )}</SB3Downloader>
                        )}
                    </div>)}
                <div className={styles.tailMenu}>
                    {/* <div
                        aria-label={this.props.intl.formatMessage(ariaMessages.community)}
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onClick={this.handleClickOpenCommunity}
                    >
                        <img
                            className={styles.communityIcon}
                            src={communityIcon}
                        />
                        {this.state.isOverflow ? null : <FormattedMessage {...ariaMessages.community} />}
                    </div>*/}
                    {/* menu-bar-modernization: standalone "Tài liệu mSTEM" (wiki) top-level
                        button REMOVED from the tail menu — it now lives as a MenuItem inside
                        the "Trợ giúp" dropdown below (handleClickOpenWiki, unchanged). This
                        de-clutters the header by one item. */}
                    {/* BRANDING 2026-06-17: native Scratch Tutorials button (TipsLibrary, English
                        Scratch/CODE.org content) hidden per project constraint — the mSTEM Vietnamese
                        guide lives in the "Trợ giúp" dropdown (PRJ-06/07). Restore by un-commenting.
                    <div
                        aria-label={this.props.intl.formatMessage(ariaMessages.tutorials)}
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onClick={this.props.onOpenTipLibrary}
                    >
                        <img
                            className={styles.helpIcon}
                            src={helpIcon}
                        />
                        {this.state.isOverflow ? null : <FormattedMessage {...ariaMessages.tutorials} />}
                    </div>
                    */}
                    {/* Phase 3 / Plan 03-09 W-13 — Help menu (NEW per DP-15).
                        Mirrors fileMenu / settingMenu dropdown pattern.
                        White help icon on navy header per micro-2.1. */}
                    <div
                        aria-label={this.props.intl.formatMessage(ariaMessages.help)}
                        className={classNames(styles.menuBarItem, styles.hoverable, {
                            [styles.active]: this.props.helpMenuOpen
                        })}
                        onMouseUp={this.handleHelpMouseUp}
                    >
                        <img
                            className={styles.helpMenuIcon}
                            draggable={false}
                            src={helpMenuIcon}
                            alt=""
                        />
                        {this.state.isOverflow ? null : (
                            <span className={styles.helpLabel}>
                                <FormattedMessage
                                    defaultMessage="Trợ giúp"
                                    description="Help menu trigger label (PRJ-06 + PRJ-07)"
                                    id="gen.menuBar.help"
                                />
                            </span>
                        )}
                        <MenuBarMenu
                            className={classNames(styles.menuBarMenu)}
                            open={this.props.helpMenuOpen}
                            place={this.props.isRtl ? 'left' : 'right'}
                            onRequestClose={this.props.onRequestCloseHelp}
                        >
                            <MenuSection>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickHelpIde}
                                >
                                    <FormattedMessage
                                        defaultMessage="Hướng dẫn sử dụng IDE"
                                        description="Help menu: IDE tutorial (PRJ-06)"
                                        id="gen.menuBar.helpIde"
                                    />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickHelpFirmware}
                                >
                                    <FormattedMessage
                                        defaultMessage="Hướng dẫn cập nhật Firmware"
                                        description="Help menu: Firmware tutorial (PRJ-07)"
                                        id="gen.menuBar.helpFirmware"
                                    />
                                </MenuItem>
                                {/* Phase 4 gap-closure (ROB-02..04) — opens the
                                    composite "what's inside" info modal. */}
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickHelpRobotComposite}
                                >
                                    <FormattedMessage
                                        defaultMessage="Xem cấu tạo khối Robot ghép"
                                        description="Help menu: open the composite Robot block info modal" // eslint-disable-line max-len
                                        id="gen.menuBar.helpRobotComposite"
                                    />
                                </MenuItem>
                                {/* Phase 5 SMH-02 — opens the SmartHome
                                    monitoring dashboard modal. */}
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickSmartHomeDashboard}
                                >
                                    <FormattedMessage
                                        defaultMessage="Dashboard giám sát thiết bị"
                                        description="Help menu: open the SmartHome monitoring dashboard (SMH-02)" // eslint-disable-line max-len
                                        id="gen.menuBar.smartHomeDashboard"
                                    />
                                </MenuItem>
                                {/* Phase 7 AI-01 — opens the AI control panel. */}
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickAiPanel}
                                >
                                    <FormattedMessage
                                        defaultMessage="Bảng điều khiển AI"
                                        description="Help menu: open the AI control panel (AI-01)"
                                        id="gen.ai.menu.title"
                                    />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickOpenWiki}
                                >
                                    <FormattedMessage
                                        defaultMessage="Tài liệu mSTEM"
                                        description="Help menu: external mSTEM docs link (kept from Phase 1)"
                                        id="gen.menuBar.helpDocs"
                                    />
                                </MenuItem>
                            </MenuSection>
                        </MenuBarMenu>
                    </div>
                    {/* Phase 6 / Plan 06-01 — "Mở rộng" (Khối Mở rộng) authoring
                        dropdown (DR-05). Mirrors the Help-menu structure verbatim;
                        4 items open the EXT-01..04 modals via openModal flags. */}
                    <div
                        className={classNames(styles.menuBarItem, styles.hoverable, {
                            [styles.active]: this.props.extMenuOpen
                        })}
                        onMouseUp={this.handleExtMouseUp}
                    >
                        {this.state.isOverflow ? null : (
                            <span className={styles.helpLabel}>
                                <FormattedMessage
                                    defaultMessage="Mở rộng"
                                    description="Extension authoring menu group title (DR-05)"
                                    id="gen.ext.menu.title"
                                />
                            </span>
                        )}
                        <MenuBarMenu
                            className={classNames(styles.menuBarMenu)}
                            open={this.props.extMenuOpen}
                            place={this.props.isRtl ? 'left' : 'right'}
                            onRequestClose={this.props.onRequestCloseExt}
                        >
                            <MenuSection>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickDeclareDevice}
                                >
                                    <FormattedMessage
                                        defaultMessage="Khai báo thiết bị mới"
                                        description="Mở rộng menu: open the device declaration wizard (EXT-01)" // eslint-disable-line max-len
                                        id="gen.ext.menu.declareDevice"
                                    />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickBuildBlock}
                                >
                                    <FormattedMessage
                                        defaultMessage="Khai báo khối mới"
                                        description="Mở rộng menu: open the block builder (EXT-02)"
                                        id="gen.ext.menu.buildBlock"
                                    />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickLedDesigner}
                                >
                                    <FormattedMessage
                                        defaultMessage="Thiết kế đèn LED 8×8"
                                        description="Mở rộng menu: open the LED 8x8 matrix designer (EXT-03)" // eslint-disable-line max-len
                                        id="gen.ext.menu.ledDesigner"
                                    />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickPublish}
                                >
                                    <FormattedMessage
                                        defaultMessage="Công bố thiết bị / tính năng"
                                        description="Mở rộng menu: open the publish wizard (EXT-04)"
                                        id="gen.ext.menu.publish"
                                    />
                                </MenuItem>
                            </MenuSection>
                            {/* UC-43 / UC-47 — manage declared devices/blocks */}
                            <MenuSection>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickManageDevices}
                                >
                                    <FormattedMessage
                                        defaultMessage="Quản lý thiết bị mở rộng"
                                        description="Mở rộng menu: open the device manager (UC-43)"
                                        id="gen.ext.menu.manageDevices"
                                    />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickManageBlocks}
                                >
                                    <FormattedMessage
                                        defaultMessage="Quản lý khối mở rộng"
                                        description="Mở rộng menu: open the block manager (UC-47)"
                                        id="gen.ext.menu.manageBlocks"
                                    />
                                </MenuItem>
                            </MenuSection>
                        </MenuBarMenu>
                    </div>
                    <Divider className={classNames(styles.divider)} />
                    <div
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onMouseUp={this.handleScreenshot}
                    >
                        <img
                            alt="Screenshot"
                            className={classNames(styles.screenShotLogo)}
                            draggable={false}
                            src={screenshotIcon}
                        />
                    </div>
                    <Divider className={classNames(styles.divider)} />
                    {/* MSTEM-ADDED Phase 2 GEN-02 — Theme manager cog icon (DG-01) */}
                    <div
                        aria-label="Theme manager"
                        className={classNames(styles.menuBarItem, styles.hoverable, styles.themeManagerButton)}
                        onMouseUp={this.props.onOpenThemeManager}
                    >
                        <img
                            alt="Theme"
                            className={classNames(styles.themeManagerIcon)}
                            draggable={false}
                            src={themeIcon}
                        />
                    </div>
                    <Divider className={classNames(styles.groupDivider)} />
                    {/* menu-bar-modernization — "Quản lý" consolidation dropdown (GEN-03/04/05).
                        Replaces 3 standalone top-level buttons (Robot Manager, Kit Manager,
                        Upload Firmware) with ONE dropdown to keep the header from overflowing.
                        Mirrors the Trợ giúp / Mở rộng dropdown markup verbatim. Each item
                        opens the SAME existing modal action — no functional change. */}
                    <div
                        aria-label={this.props.intl.formatMessage(mstemMessages.manageMenuAria)}
                        className={classNames(styles.menuBarItem, styles.hoverable, {
                            [styles.active]: this.props.manageMenuOpen
                        })}
                        onMouseUp={this.handleManageMouseUp}
                    >
                        <img
                            alt=""
                            className={classNames(styles.uploadFirmwareLogo)}
                            draggable={false}
                            src={uploadFirmwareIcon}
                        />
                        {this.state.isOverflow ? null : (
                            <span className={styles.helpLabel}>
                                <FormattedMessage {...mstemMessages.manageMenu} />
                            </span>
                        )}
                        <MenuBarMenu
                            className={classNames(styles.menuBarMenu)}
                            open={this.props.manageMenuOpen}
                            place={this.props.isRtl ? 'left' : 'right'}
                            onRequestClose={this.props.onRequestCloseManage}
                        >
                            <MenuSection>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickManageRobot}
                                >
                                    <FormattedMessage {...mstemMessages.manageRobot} />
                                </MenuItem>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickManageKit}
                                >
                                    <FormattedMessage {...mstemMessages.manageKit} />
                                </MenuItem>
                            </MenuSection>
                            <MenuSection>
                                <MenuItem
                                    isRtl={this.props.isRtl}
                                    onClick={this.handleClickManageFirmware}
                                >
                                    <img
                                        className={styles.fileMenuIcon}
                                        draggable={false}
                                        src={uploadFirmwareIcon}
                                        alt=""
                                    />
                                    <FormattedMessage {...mstemMessages.manageFirmware} />
                                </MenuItem>
                            </MenuSection>
                        </MenuBarMenu>
                    </div>
                    {/* MSTEM 2026-06-18: "Program Mode" (Upload/Realtime) Switch REMOVED.
                        It was permanently disabled (isSupportSwitchMode === false in the
                        prototype — no real device bridge) and is not an mSTEM requirement.
                        The PROGRAM_MODE_UPDATE listener + program-mode reducer stay intact for
                        the BE team; only this dead toggle UI is removed.

                        MSTEM 2026-09-04 — KHÔI PHỤC RỒI GỠ LẠI. Công tắc từng được khôi phục
                        (e66abba1) vì hiểu nhầm rằng mSTEM cần chế độ nạp của thư viện. Rà lại BRD
                        cho thấy KHÔNG PHẢI, và đây là lý do đúng — mạnh hơn nhiều so với câu
                        "not an mSTEM requirement" ở trên:

                          • BRD đặc tả điều khiển robot BẰNG KHỐI SCRATCH CHUẨN, không phải bằng
                            khối tự chế. RM-022 "Điều khiển robot tiến" → ô "Danh mục chức năng"
                            ghi nhóm Motion, ô "Bảng khối lệnh" ghi "move 10 steps, turn 15
                            degrees...", và ảnh image83.png chụp đúng bảng chọn Motion gốc.
                            Tương tự: Looks cho hiển thị chữ (RM-028/029), Sound cho âm thanh
                            (RM-031), Sensing cho cảm biến (RM-032→037).
                          • Ở chế độ nạp, openblock-vm BỎ đúng bốn nhóm Motion/Looks/Sound/Sensing
                            (bảng chọn rút 9 nhóm còn 5). Tức bật chế độ nạp = xoá sạch 66/104 khối
                            mà BRD giao cho 16 màn robot.
                          • Robot chạy được là do FIRMWARE DIỄN GIẢI gói kịch bản JSON nạp xuống
                            (RM-009, kênh MQTT .../program), không phải do IDE đổi chế độ. Công văn
                            HLD: "gói mã lệnh là kịch bản dữ liệu có cấu trúc, thiết bị diễn giải".

                        ⇒ IDE của mSTEM LUÔN ở chế độ thời gian thực. ĐỪNG khôi phục công tắc này.
                        Xem .planning/adr/013-hai-che-do-chay-ide.md */}
                    {/* MSTEM-ADDED Phase 2 DG-06 — Admin role indicator (purple pill).
                        Single-admin prototype per DG-06 — decorative only, no auth gating. */}
                    <div className={classNames(styles.menuBarItem, styles.adminBadgeSlot)}>
                        <MstemBadge variant="admin">
                            <FormattedMessage {...mstemMessages.adminBadge} />
                        </MstemBadge>
                    </div>
                    {isScratchDesktop() ? (
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, {
                                [styles.active]: this.props.settingMenuOpen
                            })}
                            onMouseUp={this.props.onClickSetting}
                        >
                            <img
                                className={styles.settingIcon}
                                src={settingIcon}
                            />
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.settingMenuOpen}
                                place={this.props.isRtl ? 'right' : 'left'}
                                onRequestClose={this.props.onRequestCloseSetting}
                            >
                                <MenuSection>
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleCheckUpdate}
                                    >
                                        {checkUpdate}
                                    </MenuItem>
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClearCache}
                                    >
                                        {clearCache}
                                    </MenuItem>
                                </MenuSection>
                                <MenuSection>
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.props.onClickInstallDriver}
                                    >
                                        {installDriver}
                                    </MenuItem>
                                </MenuSection>
                                <MenuSection>
                                    {typeof this.props.onClickAbout === 'object' ? aboutButton : null}
                                </MenuSection>
                            </MenuBarMenu>
                        </div>
                    ) : null}
                </div>
                { (typeof this.props.onClickAbout === 'function') ? aboutButton : null}
            </Box>
        );
    }
}

MenuBar.propTypes = {
    aboutMenuOpen: PropTypes.bool,
    accountMenuOpen: PropTypes.bool,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    confirmClearCache: PropTypes.func,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    settingMenuOpen: PropTypes.bool,
    intl: intlShape,
    isUpdating: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isToolboxUpdating: PropTypes.bool.isRequired,
    languageMenuOpen: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    loginMenuOpen: PropTypes.bool,
    logo: PropTypes.string,
    logoSmall: PropTypes.string,
    onClickAbout: PropTypes.oneOfType([
        PropTypes.func, // button mode: call this callback when the About button is clicked
        PropTypes.arrayOf( // menu mode: list of items in the About menu
            PropTypes.shape({
                title: PropTypes.string, // text for the menu item
                onClick: PropTypes.func // call this callback when the menu item is clicked
            })
        )
    ]),
    onClickAccount: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickSetting: PropTypes.func,
    onClickLanguage: PropTypes.func,
    onClickLogin: PropTypes.func,
    onClickLogo: PropTypes.func,
    // BLOCKER #3 (Plan 03-09): Scratch FSM new/save prop callbacks REMOVED —
    // Phase 3 menu-bar uses onOpenNewProjectModal + handleClickSavePhase3 instead.
    onClickRemix: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onClickCheckUpdate: PropTypes.func,
    onClickClearCache: PropTypes.func,
    onClickInstallDriver: PropTypes.func,
    onLogOut: PropTypes.func,
    onNoPeripheralIsConnected: PropTypes.func.isRequired,
    onOpenRegistration: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestOpenAbout: PropTypes.func,
    onRequestCloseAbout: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseSetting: PropTypes.func,
    onRequestCloseLanguage: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    projectTitle: PropTypes.string,
    realtimeConnection: PropTypes.bool.isRequired,
    renderLogin: PropTypes.func,
    sessionExists: PropTypes.bool,
    shouldSaveBeforeTransition: PropTypes.func,
    showComingSoon: PropTypes.bool,
    userOwnsProject: PropTypes.bool,
    username: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired,
    onSetUploadMode: PropTypes.func,
    onSetRealtimeConnection: PropTypes.func.isRequired,
    onSetRealtimeMode: PropTypes.func,
    onSetUpdate: PropTypes.func.isRequired,
    onOpenConnectionModal: PropTypes.func,
    onOpenUploadProgress: PropTypes.func,
    peripheralName: PropTypes.string,
    onDisconnect: PropTypes.func.isRequired,
    onWorkspaceIsEmpty: PropTypes.func.isRequired,
    onWorkspaceIsNotEmpty: PropTypes.func.isRequired,
    onOpenDeviceLibrary: PropTypes.func,
    onOpenThemeManager: PropTypes.func,
    onOpenRobotManager: PropTypes.func,
    onOpenKitManager: PropTypes.func,
    onOpenFirmwareWizard: PropTypes.func,
    deviceId: PropTypes.string,
    deviceName: PropTypes.string,
    onDeviceIsEmpty: PropTypes.func,
    // Phase 3 / Plan 03-09 additions (PRJ-01..07).
    currentProjectId: PropTypes.string,
    myProjectsList: PropTypes.arrayOf(PropTypes.object),
    helpMenuOpen: PropTypes.bool,
    onClickHelp: PropTypes.func,
    onRequestCloseHelp: PropTypes.func,
    onOpenNewProjectModal: PropTypes.func,
    onOpenOpenProjectModal: PropTypes.func,
    onOpenSaveProjectModal: PropTypes.func,
    onOpenShareProjectModal: PropTypes.func,
    onOpenSharedProjectsModal: PropTypes.func,
    onOpenTutorialModal: PropTypes.func,
    onOpenTutorial: PropTypes.func,
    onOpenRobotBlockInfo: PropTypes.func,
    onOpenSmartHomeDashboard: PropTypes.func,
    onOpenAiPanel: PropTypes.func,
    // Phase 6 / Plan 06-01 — "Mở rộng" dropdown + 4 EXT modal opens.
    extMenuOpen: PropTypes.bool,
    onClickExt: PropTypes.func,
    onRequestCloseExt: PropTypes.func,
    // menu-bar-modernization — "Quản lý" consolidation dropdown state + handlers.
    manageMenuOpen: PropTypes.bool,
    onClickManage: PropTypes.func,
    onRequestCloseManage: PropTypes.func,
    onOpenDeviceDeclaration: PropTypes.func,
    onOpenBlockBuilder: PropTypes.func,
    onOpenLedDesigner: PropTypes.func,
    onOpenPublishWizard: PropTypes.func,
    // UC-43 / UC-47 — manager modal opens.
    onOpenExtDeviceManager: PropTypes.func,
    onOpenExtBlockManager: PropTypes.func,
    onSetRobotBlockInfoKey: PropTypes.func,
    onUpdateEntry: PropTypes.func,
    onShowSaved: PropTypes.func,
    onShowTooLarge: PropTypes.func,
    onShowQuotaFull: PropTypes.func
};

MenuBar.defaultProps = {
    logo: openblockLogo,
    logoSmall: openblockLogoSmall,
    onShare: () => {}
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user = state.session && state.session.session && state.session.session.user;
    return {
        aboutMenuOpen: aboutMenuOpen(state),
        accountMenuOpen: accountMenuOpen(state),
        fileMenuOpen: fileMenuOpen(state),
        settingMenuOpen: settingMenuOpen(state),
        isUpdating: getIsUpdating(loadingState),
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        isToolboxUpdating: state.scratchGui.toolbox.isToolboxUpdating,
        languageMenuOpen: languageMenuOpen(state),
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        realtimeConnection: state.scratchGui.connectionModal.realtimeConnection,
        sessionExists: state.session && typeof state.session.session !== 'undefined',
        username: user ? user.username : null,
        userOwnsProject: ownProps.authorUsername && user &&
            (ownProps.authorUsername === user.username),
        vm: state.scratchGui.vm,
        peripheralName: state.scratchGui.connectionModal.peripheralName,
        deviceId: state.scratchGui.device.deviceId,
        deviceName: state.scratchGui.device.deviceName,
        // Phase 3 / Plan 03-09 additions (BLOCKER #2 — Ctrl+S quick-save reads
        // myProjects from props via connect, never via direct store access).
        myProjectsList: (state.scratchGui.myProjects && state.scratchGui.myProjects.list) || [],
        currentProjectId: (state.scratchGui.myProjects && state.scratchGui.myProjects.currentId) || null,
        helpMenuOpen: helpMenuOpen(state),
        // Phase 6 / Plan 06-01 — "Mở rộng" dropdown open state.
        extMenuOpen: extMenuOpen(state),
        // menu-bar-modernization — "Quản lý" consolidation dropdown open state.
        manageMenuOpen: manageMenuOpen(state)
    };
};

const mapDispatchToProps = dispatch => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onClickAccount: () => dispatch(openAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(openFileMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onClickSetting: () => dispatch(openSettingMenu()),
    onRequestCloseSetting: () => dispatch(closeSettingMenu()),
    onClickLanguage: () => dispatch(openLanguageMenu()),
    onRequestCloseLanguage: () => dispatch(closeLanguageMenu()),
    onClickLogin: () => dispatch(openLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onRequestOpenAbout: () => dispatch(openAboutMenu()),
    onRequestCloseAbout: () => dispatch(closeAboutMenu()),
    // BLOCKER #3 (Plan 03-09): REMOVED Scratch FSM dispatchers for "new" +
    // "save"; Phase 3 menu-bar uses modal-based pathways instead
    // (onOpenNewProjectModal + handleClickSavePhase3).
    onClickRemix: () => dispatch(remixProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    onSetUploadMode: () => {
        dispatch(setUploadMode());
        dispatch(setRealtimeConnection(false));
    },
    onSetRealtimeConnection: state => dispatch(setRealtimeConnection(state)),
    onSetRealtimeMode: () => dispatch(setRealtimeMode()),
    onOpenConnectionModal: () => dispatch(openConnectionModal()),
    onOpenUploadProgress: () => dispatch(openUploadProgress()),
    onDisconnect: () => {
        dispatch(clearConnectionModalPeripheralName());
        dispatch(setRealtimeConnection(false));
    },
    onSetUpdate: message => {
        dispatch(setUpdate(message));
        dispatch(openUpdateModal());
    },
    onNoPeripheralIsConnected: () => showAlertWithTimeout(dispatch, 'connectAPeripheralFirst'),
    onWorkspaceIsEmpty: () => showAlertWithTimeout(dispatch, 'workspaceIsEmpty'),
    onWorkspaceIsNotEmpty: () => showAlertWithTimeout(dispatch, 'workspaceIsNotEmpty'),
    onOpenDeviceLibrary: () => dispatch(openDeviceLibrary()),
    onOpenThemeManager: () => dispatch(openThemeManager()),
    onOpenRobotManager: () => dispatch(openRobotManager()),
    onOpenKitManager: () => dispatch(openKitManager()),
    onOpenFirmwareWizard: () => dispatch(openFirmwareWizard()),
    onDeviceIsEmpty: () => showAlertWithTimeout(dispatch, 'selectADeviceFirst'),
    // Phase 3 / Plan 03-09 — 6 modal opens + tutorial selection (PRJ-01..07).
    onOpenNewProjectModal: () => dispatch(openNewProjectModalAction()),
    onOpenOpenProjectModal: () => dispatch(openOpenProjectModalAction()),
    onOpenSaveProjectModal: () => dispatch(openSaveProjectModalAction()),
    onOpenShareProjectModal: () => dispatch(openShareProjectModalAction()),
    onOpenSharedProjectsModal: () => dispatch(openSharedProjectsModalAction()),
    onOpenTutorialModal: () => dispatch(openTutorialModalAction()),
    onOpenTutorial: (type, chapterId) => dispatch(openTutorial(type, chapterId)),
    // Phase 4 gap-closure — open the composite info modal from the Help menu.
    onOpenRobotBlockInfo: () => dispatch(openRobotBlockInfoAction()),
    onSetRobotBlockInfoKey: key => dispatch(setRobotBlockInfoKeyAction(key)),
    // Phase 5 SMH-02 — open the SmartHome dashboard from the Help menu.
    onOpenSmartHomeDashboard: () => dispatch(openSmartHomeDashboardAction()),
    onOpenAiPanel: () => dispatch(openAiPanelAction()),
    // Phase 3 / Plan 03-09 (BLOCKER #2) — Ctrl+S quick-save dispatchers.
    onUpdateEntry: entry => dispatch(updateProjectEntry(entry)),
    onShowSaved: () => showAlertWithTimeout(dispatch, 'projectSaved'),
    onShowTooLarge: () => showAlertWithTimeout(dispatch, 'projectTooLarge'),
    onShowQuotaFull: () => showAlertWithTimeout(dispatch, 'localStorageFull'),
    // Phase 3 / Plan 03-09 W-13 — Help dropdown state mirrors fileMenu pattern.
    onClickHelp: () => dispatch(openHelpMenu()),
    onRequestCloseHelp: () => dispatch(closeHelpMenu()),
    // Phase 6 / Plan 06-01 — "Mở rộng" dropdown + 4 EXT modal opens (EXT-01..04).
    onClickExt: () => dispatch(openExtMenu()),
    onRequestCloseExt: () => dispatch(closeExtMenu()),
    onOpenDeviceDeclaration: () => dispatch(openDeviceDeclarationAction()),
    onOpenBlockBuilder: () => dispatch(openBlockBuilderAction()),
    onOpenExtDeviceManager: () => dispatch(openExtDeviceManagerAction()),
    onOpenExtBlockManager: () => dispatch(openExtBlockManagerAction()),
    onOpenLedDesigner: () => dispatch(openLedDesignerAction()),
    onOpenPublishWizard: () => dispatch(openPublishWizardAction()),
    // menu-bar-modernization — "Quản lý" dropdown state mirrors extMenu pattern.
    // (onOpenRobotManager / onOpenKitManager / onOpenFirmwareWizard already wired above.)
    onClickManage: () => dispatch(openManageMenu()),
    onRequestCloseManage: () => dispatch(closeManageMenu())
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(MenuBar);
